import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect, useCallback } from "react";
import { BookOpen, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CppeActivity {
  id: number;
  title: string;
  date: string;
  hours: number;
  category: string;
  learningOutcome: string;
  patientBenefit: string;
  createdAt: string;
}

const CPPE_PROGRAMMES = [
  {
    title: "DMARDs in Rheumatology",
    desc: "Comprehensive module covering disease-modifying antirheumatic drugs including methotrexate, sulfasalazine, hydroxychloroquine, and leflunomide — monitoring requirements, patient counselling, and interactions.",
    url: "https://www.cppe.ac.uk/",
    badge: "Recommended",
  },
  {
    title: "Biologics and Biosimilars in Inflammatory Arthritis",
    desc: "Covers TNF inhibitors, IL-6 inhibitors, B-cell depletion, and JAK inhibitors. Biosimilar switching, nocebo effect, NHSE policy, and pharmacovigilance for biologic DMARDs.",
    url: "https://www.cppe.ac.uk/",
    badge: "Recommended",
  },
  {
    title: "Medicines Reconciliation",
    desc: "NICE guidance-based programme on identifying and resolving medication discrepancies at care transitions. Relevant to rheumatology patients on complex polypharmacy.",
    url: "https://www.cppe.ac.uk/",
    badge: "",
  },
  {
    title: "Clinical Pharmacy in Secondary Care",
    desc: "Pharmacy practice in hospital settings including ward rounds, medicines optimisation, clinical decision-making, and MDT working. Supports specialist pharmacist development.",
    url: "https://www.cppe.ac.uk/",
    badge: "",
  },
  {
    title: "Corticosteroids",
    desc: "Safe prescribing and monitoring of systemic corticosteroids — adrenal suppression, steroid-sparing strategies, bone protection, and sick day rules relevant to rheumatology patients.",
    url: "https://www.cppe.ac.uk/",
    badge: "",
  },
  {
    title: "Immunosuppressants",
    desc: "Covers azathioprine, mycophenolate, ciclosporin, and tacrolimus in non-transplant settings — monitoring, interactions, TPMT testing, and patient counselling.",
    url: "https://www.cppe.ac.uk/",
    badge: "",
  },
  {
    title: "Non-Medical Prescribing",
    desc: "Supports pharmacists in developing or maintaining their independent prescribing practice. Covers clinical assessment, governance, and competency framework alignment.",
    url: "https://www.cppe.ac.uk/",
    badge: "NMP",
  },
  {
    title: "Renal Impairment and Medicines",
    desc: "Dose adjustment and contraindications for medicines in renal impairment — essential for rheumatology where many DMARDs require renal dose adjustment.",
    url: "https://www.cppe.ac.uk/",
    badge: "",
  },
];

interface AddFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

function AddCppeForm({ onSaved, onCancel }: AddFormProps) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [hours, setHours] = useState("");
  const [learningOutcome, setLearningOutcome] = useState("");
  const [patientBenefit, setPatientBenefit] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date || !hours || !learningOutcome.trim() || !patientBenefit.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/pharmacist/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          type: "act",
          category: "professional",
          hours: parseFloat(hours),
          learningOutcome: learningOutcome.trim(),
          patientBenefit: patientBenefit.trim(),
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSaved();
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-violet-200 p-6 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">Log CPPE Completion</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="cppe-title" className="block text-xs font-medium text-slate-600 mb-1.5">Programme / Activity Title *</label>
          <input
            id="cppe-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. DMARDs in Rheumatology — CPPE e-learning"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div>
          <label htmlFor="cppe-date" className="block text-xs font-medium text-slate-600 mb-1.5">Completion Date *</label>
          <input
            id="cppe-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div>
          <label htmlFor="cppe-hours" className="block text-xs font-medium text-slate-600 mb-1.5">Hours *</label>
          <input
            id="cppe-hours"
            type="number"
            min="0.5"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="e.g. 2"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="cppe-learned" className="block text-xs font-medium text-slate-600 mb-1.5">What I Learned *</label>
          <textarea
            id="cppe-learned"
            value={learningOutcome}
            onChange={(e) => setLearningOutcome(e.target.value)}
            rows={2}
            placeholder="Key knowledge or skills gained from this CPPE programme"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="cppe-benefit" className="block text-xs font-medium text-slate-600 mb-1.5">Benefit to Patients / Public *</label>
          <textarea
            id="cppe-benefit"
            value={patientBenefit}
            onChange={(e) => setPatientBenefit(e.target.value)}
            rows={2}
            placeholder="How this learning benefits patient care in your rheumatology practice"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-violet-700 hover:bg-violet-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {saving ? "Saving..." : "Log Completion"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}

export default function CppeLearning() {
  const [activities, setActivities] = useState<CppeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expandedProg, setExpandedProg] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/pharmacist/cpd?year=${new Date().getFullYear()}`);
      if (res.ok) {
        const data: CppeActivity[] = await res.json();
        setActivities(data.filter((a: { category: string }) => a.category === "professional"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this CPPE log entry?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/pharmacist/cpd/${id}`, { method: "DELETE" });
      setActivities((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PharmacistLayout title="CPPE Learning">
      <div className="space-y-8">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">CPPE Learning Hub</h2>
              <p className="text-violet-300 text-sm">Centre for Pharmacy Postgraduate Education · Rheumatology CPD</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            CPPE provides free, high-quality CPD for NHS pharmacists. Below are key programmes relevant to rheumatology specialist pharmacy practice. Visit the CPPE website to access programmes, then log your completions here as CPD evidence.
          </p>
          <a
            href="https://www.cppe.ac.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-sm text-violet-200 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Visit cppe.ac.uk
          </a>
        </div>

        {/* CPPE programmes */}
        <div>
          <h3 className="font-bold text-slate-900 mb-3">Recommended CPPE Programmes for Rheumatology</h3>
          <div className="space-y-2">
            {CPPE_PROGRAMMES.map((prog, i) => {
              const open = expandedProg === i;
              return (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setExpandedProg(open ? null : i)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">{prog.title}</span>
                        {prog.badge && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">{prog.badge}</span>
                        )}
                      </div>
                    </div>
                    {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                      <p className="text-sm text-slate-600 leading-relaxed mb-3">{prog.desc}</p>
                      <a
                        href={prog.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Access on CPPE website
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity log */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900">My CPPE Completion Log ({new Date().getFullYear()})</h3>
            <Button onClick={() => setShowForm(true)} className="bg-violet-700 hover:bg-violet-800 text-white gap-2">
              <Plus className="h-4 w-4" />
              Log Completion
            </Button>
          </div>

          {showForm && (
            <div className="mb-4">
              <AddCppeForm onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-xl" />)}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 rounded-2xl text-slate-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">No CPPE completions logged this year</p>
              <p className="text-xs mt-1">Complete a CPPE programme and log it above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((a) => {
                const expanded = expandedId === a.id;
                return (
                  <div key={a.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div
                      className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedId(expanded ? null : a.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(expanded ? null : a.id); }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">{a.title}</p>
                        <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {a.hours}h</p>
                      </div>
                      {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                    </div>
                    {expanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">What I Learned</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{a.learningOutcome}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">Benefit to Patients</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{a.patientBenefit}</p>
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleDelete(a.id)}
                            disabled={deletingId === a.id}
                            className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            {deletingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>About CPPE:</strong> The Centre for Pharmacy Postgraduate Education (CPPE) provides free CPD and training resources for pharmacy professionals in England, commissioned by NHS England. CPPE completions logged here are recorded as CPD activity entries (category: professional) and count towards your GPhC annual CPD target.
        </div>
      </div>
    </PharmacistLayout>
  );
}
